// src/Pages/Courses/Course.jsx
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Parallax } from 'react-scroll-parallax';
import { m } from 'framer-motion';
import { fadeIn } from '../../Functions/GlobalAnimations';
import { Grid, List, SlidersHorizontal, ChevronLeft, ChevronRight } from 'lucide-react';
import config from '../../config';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSearchParams } from 'react-router-dom';
import ImageWithAuth from '../../Components/ImageWithAuth';

const Course = (props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';
  const [searchParams] = useSearchParams();

  // CONSTANTES pour les classes qui nécessitent une spécialité
  const CLASSES_REQUIRING_SPECIALITE = [
    "687d5b1fe74694aa1bf8572b", // 2ème secondaire
    "687d5b30e74694aa1bf85735"  // 3ème secondaire
  ];

  useEffect(() => {
    const querySearch = searchParams.get('search');
    if (querySearch) {
      setSearchTerm(decodeURIComponent(querySearch));
    }
  }, [searchParams]);
  
  // States
  const [documents, setDocuments] = useState([]);
  const [levelCategories, setLevelCategories] = useState({});
  const [subLevelsData, setSubLevelsData] = useState({});
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState([0, 0]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  const [contentTypes, setContentTypes] = useState([]);
  const [quarters, setQuarters] = useState([]);
  const [selectedContentTypes, setSelectedContentTypes] = useState([]);
  const [selectedQuarters, setSelectedQuarters] = useState([]);
  
  const [specialitesByClass, setSpecialitesByClass] = useState({});
  const [selectedSpecialiteNames, setSelectedSpecialiteNames] = useState([]);
  const [isLoadingSpecialites, setIsLoadingSpecialites] = useState(false);

  const deduplicateDocuments = (docs) => {
    const uniqueDocs = [];
    const seenIds = new Set();
    docs.forEach(doc => {
      if (!seenIds.has(doc.id)) {
        seenIds.add(doc.id);
        uniqueDocs.push(doc);
      }
    });
    return uniqueDocs;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedLevels, selectedCategories, selectedSubjects,
    selectedContentTypes, selectedQuarters, selectedSpecialiteNames,
    searchTerm, priceRange, sortBy, viewMode
  ]);

  // Vérifier si une classe sélectionnée nécessite une spécialité
  const hasClassRequiringSpecialite = useCallback(() => {
    return selectedCategories.some(categoryName => {
      const classEntry = Object.entries(subLevelsData).find(
        ([_, data]) => data.name === categoryName
      );
      const classId = classEntry?.[0];
      return classId && CLASSES_REQUIRING_SPECIALITE.includes(classId);
    });
  }, [selectedCategories, subLevelsData]);

  const loadSpecialitesForClassIds = useCallback(async (classIds) => {
    if (!classIds || classIds.length === 0) return {};
    
    // Filtrer uniquement les IDs des classes qui nécessitent une spécialité
    const filteredClassIds = classIds.filter(id => 
      CLASSES_REQUIRING_SPECIALITE.includes(id)
    );
    
    if (filteredClassIds.length === 0) return {};
    
    const idsToLoad = filteredClassIds.filter(id => {
      return !specialitesByClass[id] || specialitesByClass[id].length === 0;
    });
    
    if (idsToLoad.length === 0) {
      const existingData = {};
      filteredClassIds.forEach(id => {
        if (specialitesByClass[id]) {
          existingData[id] = specialitesByClass[id];
        }
      });
      return existingData;
    }
    
    setIsLoadingSpecialites(true);
    const results = {};
    
    try {
      for (const classId of idsToLoad) {
        const response = await axios.get(`${config.SITE_API_URL}/getbysublevel_specialitesublevel/${classId}`, {
          headers: {
            'x-api-key': config.SITE_API_KEY,
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        });
        
        const specialites = response.data.data || [];
        const formattedSpecialites = specialites.map(spec => ({
          id: spec._id,
          name: spec.name?.[i18n.language] || spec.name?.fr || '',
          nameAr: spec.name?.ar || '',
          sublevelId: spec.sublevel?._id || spec.sublevel
        }));
        
        results[classId] = formattedSpecialites;
      }
      
      setSpecialitesByClass(prev => ({ ...prev, ...results }));
      return results;
    } catch (error) {
      console.error('Error loading specialites:', error);
      return {};
    } finally {
      setIsLoadingSpecialites(false);
    }
  }, [i18n.language, specialitesByClass]);

  // Fetch sub-levels
  useEffect(() => {
    const fetchSubLevels = async () => {
      try {
        const res = await axios.get(`${config.SITE_API_URL}/actif_sublevel`, {
          headers: {
            'x-api-key': config.SITE_API_KEY,
            'Content-Type': 'application/json',
          },
          params: { lang: i18n.language },
        });
        
        const grouped = {};
        const subLevelsInfo = {};
        
        res.data.forEach(sub => {
          const lvlName = sub.level?.name?.[i18n.language] || sub.level?.name?.fr || 'Inconnu';
          const subName = sub.name?.[i18n.language] || sub.name?.fr || '';
          const subId = sub._id;
          const levelId = sub.level?._id;
          
          if (!grouped[lvlName]) {
            grouped[lvlName] = [];
          }
          
          if (sub.isActive && subName && !grouped[lvlName].includes(subName)) {
            grouped[lvlName].push(subName);
          }
          
          subLevelsInfo[subId] = {
            id: subId,
            name: subName,
            levelName: lvlName,
            levelId: levelId,
            isActive: sub.isActive
          };
        });
        
        setLevelCategories(grouped);
        setSubLevelsData(subLevelsInfo);
      } catch (error) {
        console.error('Error fetching sub-levels:', error);
      }
    };

    fetchSubLevels();
  }, [i18n.language]);

  // Fetch documents avec enrichissement des spécialités
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response = await axios.get(`${config.SITE_API_URL}/getalldocument`, {
          headers: {
            'x-api-key': config.SITE_API_KEY,
            'Content-Type': 'application/json',
          },
          params: { lang: i18n.language },
        });
        const fetchedDocuments = response.data.result;

        const mappedDocuments = fetchedDocuments.map((doc) => {
          // Extraire les noms des spécialités du document
          let specialiteNames = [];
          let specialiteIds = [];
          
          if (Array.isArray(doc.specialite_sub_level) && doc.specialite_sub_level.length > 0) {
            specialiteNames = doc.specialite_sub_level.map(s => 
              s?.name?.[i18n.language] || s?.name?.fr || ''
            ).filter(Boolean);
            
            specialiteIds = doc.specialite_sub_level.map(s => s._id || s).filter(Boolean);
          } else if (doc.specialite_sub_level?._id) {
            specialiteNames = [doc.specialite_sub_level.name?.[i18n.language] || doc.specialite_sub_level.name?.fr || ''].filter(Boolean);
            specialiteIds = [doc.specialite_sub_level._id];
          } else if (typeof doc.specialite_sub_level === 'string') {
            specialiteIds = [doc.specialite_sub_level];
          }
          
          return {
            id: doc._id,
            title: doc.title?.[i18n.language] || doc.title?.fr || doc.title || '',
            description: doc.description?.[i18n.language] || doc.description?.fr || doc.description || '',
            price: doc.price_ttc || 0,
            level: [...new Set(doc.level?.map(l => 
              l?.name?.[i18n.language] || l?.name?.fr || ''
            ).filter(Boolean))] || [],
            category: [...new Set(doc.sub_level?.map(s => 
              s?.name?.[i18n.language] || s?.name?.fr || ''
            ).filter(Boolean))] || [],
            subject: doc.matiere?.name?.[i18n.language] || doc.matiere?.name?.fr || '',
            contentType: doc.content?.type?.name?.[i18n.language] || doc.content?.type?.name?.fr || '',
            quarter: doc.quarter?.name?.[i18n.language] || doc.quarter?.name?.fr || '',
            pages: doc.pages || 100,
            format: 'PDF',
            downloads: doc.downloads || "",
            best_seller: doc.best_seller || "",
            news: doc.news || "",
            discount: doc.taux_discount || "",
            image: doc.picture ? `${config.SITE_API_URL}${doc.picture}` : '/placeholder.svg?height=200&width=150',
            featured: doc.best_seller || doc.news || false,
            subLevelIds: doc.sub_level?.map(s => s._id).filter(Boolean) || [],
            specialiteId: specialiteIds.length > 0 ? specialiteIds[0] : null,
            specialiteNames: specialiteNames,
            allSpecialiteIds: specialiteIds
          };
        });

        const uniqueDocuments = deduplicateDocuments(mappedDocuments);
        setDocuments(uniqueDocuments);
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    };

    fetchDocuments();
  }, [i18n.language]);

  const maxPrice = useMemo(() => {
    if (documents.length === 0) return 0;
    const highest = Math.max(...documents.map(doc => Number(doc.price) || 0));
    return Math.ceil(highest);
  }, [documents]);
  
  useEffect(() => {
    if (maxPrice > 0) {
      setPriceRange(prev => {
        const newMax = Math.max(prev[1], maxPrice);
        return [prev[0], newMax];
      });
    }
  }, [maxPrice]);

  useEffect(() => {
    const fetchExtras = async () => {
      try {
        const [ctRes, qRes] = await Promise.all([
          axios.get(`${config.SITE_API_URL}/actifcontent`, {
            headers: { 'x-api-key': config.SITE_API_KEY },
            params: { lang: i18n.language },
          }),
          axios.get(`${config.SITE_API_URL}/active_quarter`, {
            headers: { 'x-api-key': config.SITE_API_KEY },
            params: { lang: i18n.language },
          }),
        ]);

        const uniqueContentTypes = ctRes.data.filter((ct, index, self) =>
          index === self.findIndex(c => 
            (c.type?.name?.[i18n.language] || c.type?.name?.fr) === 
            (ct.type?.name?.[i18n.language] || ct.type?.name?.fr)
          )
        );
        
        const uniqueQuarters = qRes.data.filter((q, index, self) =>
          index === self.findIndex(quarter => 
            (quarter.name?.[i18n.language] || quarter.name?.fr) === 
            (q.name?.[i18n.language] || q.name?.fr)
          )
        );

        setContentTypes(uniqueContentTypes);
        setQuarters(uniqueQuarters);
      } catch (err) {
        console.error('Erreur fetch content/quarters:', err);
      }
    };
    fetchExtras();
  }, [i18n.language]);

  const handleLevelSelect = async (levelName) => {
    const isSelected = selectedLevels.includes(levelName);
    
    if (isSelected) {
      const newSelectedLevels = selectedLevels.filter(l => l !== levelName);
      setSelectedLevels(newSelectedLevels);
      setSelectedCategories([]);
      setSelectedSpecialiteNames([]);
    } else {
      const newSelectedLevels = [...selectedLevels, levelName];
      setSelectedLevels(newSelectedLevels);
      setSelectedCategories([]);
      setSelectedSpecialiteNames([]);
      
      const classIds = Object.entries(subLevelsData)
        .filter(([_, data]) => data.levelName === levelName)
        .map(([id]) => id);
      
      if (classIds.length > 0) {
        await loadSpecialitesForClassIds(classIds);
      }
    }
  };

  const handleCategorySelect = async (categoryName, levelName) => {
    const isSelected = selectedCategories.includes(categoryName);
    
    if (isSelected) {
      const newSelectedCategories = selectedCategories.filter(c => c !== categoryName);
      setSelectedCategories(newSelectedCategories);
      // Ne réinitialiser les spécialités que si aucune classe nécessitant une spécialité n'est sélectionnée
      const remainingCategories = newSelectedCategories;
      const hasRequired = remainingCategories.some(cat => {
        const entry = Object.entries(subLevelsData).find(([_, data]) => data.name === cat);
        return entry && CLASSES_REQUIRING_SPECIALITE.includes(entry[0]);
      });
      if (!hasRequired) {
        setSelectedSpecialiteNames([]);
      }
    } else {
      const newSelectedCategories = [...selectedCategories, categoryName];
      setSelectedCategories(newSelectedCategories);
      
      const classEntry = Object.entries(subLevelsData).find(
        ([_, data]) => data.name === categoryName && data.levelName === levelName
      );
      const classId = classEntry?.[0];
      
      if (classId && CLASSES_REQUIRING_SPECIALITE.includes(classId)) {
        await loadSpecialitesForClassIds([classId]);
      }
    }
  };

  const toggleSpecialite = useCallback((specialiteName) => {
    setSelectedSpecialiteNames(prev => 
      prev.includes(specialiteName) 
        ? prev.filter(name => name !== specialiteName)
        : [...prev, specialiteName]
    );
  }, []);

  const toggleSubject = useCallback((sub) => {
    setSelectedSubjects(prev => prev.includes(sub) ? prev.filter(s => s !== sub) : [...prev, sub]);
  }, []);

  const toggleContentType = useCallback((ct) => {
    setSelectedContentTypes(prev => prev.includes(ct) ? prev.filter(c => c !== ct) : [...prev, ct]);
  }, []);

  const toggleQuarter = useCallback((q) => {
    setSelectedQuarters(prev => prev.includes(q) ? prev.filter(quarter => quarter !== q) : [...prev, q]);
  }, []);

  // Fonctions de comptage statiques (pour les niveaux et classes)
  const countDocumentsByLevel = useCallback((levelName) => {
    const uniqueDocIds = new Set();
    return documents.reduce((total, doc) => {
      if (doc.level?.includes(levelName) && !uniqueDocIds.has(doc.id)) {
        uniqueDocIds.add(doc.id);
        return total + 1;
      }
      return total;
    }, 0);
  }, [documents]);

  const countDocumentsByCategory = useCallback((categoryName) => {
    const uniqueDocIds = new Set();
    return documents.reduce((total, doc) => {
      if (doc.category?.includes(categoryName) && !uniqueDocIds.has(doc.id)) {
        uniqueDocIds.add(doc.id);
        return total + 1;
      }
      return total;
    }, 0);
  }, [documents]);

  // Fonction générique pour obtenir les documents filtrés selon les sélections actuelles
  const getFilteredDocumentsForCounting = useCallback(() => {
    let filtered = [...documents];

    if (selectedLevels.length) {
      filtered = filtered.filter(doc => 
        Array.isArray(doc.level) && 
        doc.level.some(lvl => selectedLevels.includes(lvl))
      );
    }

    if (selectedCategories.length) {
      filtered = filtered.filter(doc => 
        Array.isArray(doc.category) && 
        doc.category.some(cat => selectedCategories.includes(cat))
      );
    }

    if (selectedSpecialiteNames.length) {
      filtered = filtered.filter(doc => {
        if (doc.specialiteNames && doc.specialiteNames.length > 0) {
          return doc.specialiteNames.some(name => 
            selectedSpecialiteNames.includes(name)
          );
        }
        return false;
      });
    }

    if (selectedSubjects.length) {
      filtered = filtered.filter(doc => selectedSubjects.includes(doc.subject));
    }

    if (selectedContentTypes.length) {
      filtered = filtered.filter(doc => selectedContentTypes.includes(doc.contentType));
    }

    if (selectedQuarters.length) {
      filtered = filtered.filter(doc => selectedQuarters.includes(doc.quarter));
    }

    return filtered;
  }, [documents, selectedLevels, selectedCategories, selectedSpecialiteNames, selectedSubjects, selectedContentTypes, selectedQuarters]);

  // Comptage dynamique des matières
  const countDocumentsBySubjectDynamic = useCallback((subjectName) => {
    const filtered = getFilteredDocumentsForCounting();
    const uniqueDocIds = new Set();
    return filtered.reduce((total, doc) => {
      if (doc.subject === subjectName && !uniqueDocIds.has(doc.id)) {
        uniqueDocIds.add(doc.id);
        return total + 1;
      }
      return total;
    }, 0);
  }, [getFilteredDocumentsForCounting]);

  // Comptage dynamique des types de contenu
  const countDocumentsByContentTypeDynamic = useCallback((contentType) => {
    const filtered = getFilteredDocumentsForCounting();
    const uniqueDocIds = new Set();
    return filtered.reduce((total, doc) => {
      if (doc.contentType === contentType && !uniqueDocIds.has(doc.id)) {
        uniqueDocIds.add(doc.id);
        return total + 1;
      }
      return total;
    }, 0);
  }, [getFilteredDocumentsForCounting]);

  // Comptage dynamique des trimestres
  const countDocumentsByQuarterDynamic = useCallback((quarterName) => {
    const filtered = getFilteredDocumentsForCounting();
    const uniqueDocIds = new Set();
    return filtered.reduce((total, doc) => {
      if (doc.quarter === quarterName && !uniqueDocIds.has(doc.id)) {
        uniqueDocIds.add(doc.id);
        return total + 1;
      }
      return total;
    }, 0);
  }, [getFilteredDocumentsForCounting]);

  // Filtrage
  const filteredDocuments = useMemo(() => {
    let result = [...documents];

    if (selectedLevels.length) {
      result = result.filter(doc => 
        Array.isArray(doc.level) && 
        doc.level.some(lvl => selectedLevels.includes(lvl))
      );
    }

    if (selectedCategories.length) {
      result = result.filter(doc => 
        Array.isArray(doc.category) && 
        doc.category.some(cat => selectedCategories.includes(cat))
      );
    }

    if (selectedSpecialiteNames.length) {
      result = result.filter(doc => {
        if (doc.specialiteNames && doc.specialiteNames.length > 0) {
          return doc.specialiteNames.some(name => 
            selectedSpecialiteNames.includes(name)
          );
        }
        return false;
      });
    }

    if (selectedSubjects.length) {
      result = result.filter(doc => selectedSubjects.includes(doc.subject));
    }

    if (selectedContentTypes.length) {
      result = result.filter(doc => selectedContentTypes.includes(doc.contentType));
    }

    if (selectedQuarters.length) {
      result = result.filter(doc => selectedQuarters.includes(doc.quarter));
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(doc =>
        doc.title.toLowerCase().includes(term) ||
        doc.description.toLowerCase().includes(term)
      );
    }

    result = result.filter(doc => doc.price >= priceRange[0] && doc.price <= priceRange[1]);

    if (sortBy === 'new') result = result.filter(doc => doc.news);
    if (sortBy === 'featured') result = result.filter(doc => doc.best_seller);
    if (sortBy === 'price-low') result.sort((a, b) => a.price - b.price);
    if (sortBy === 'price-high') result.sort((a, b) => b.price - a.price);

    return deduplicateDocuments(result);
  }, [
    documents, selectedLevels, selectedCategories, selectedSubjects,
    selectedContentTypes, selectedQuarters, selectedSpecialiteNames,
    searchTerm, priceRange, sortBy
  ]);

  const paginatedDocuments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredDocuments.slice(startIndex, endIndex);
  }, [filteredDocuments, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      if (currentPage <= 3) endPage = 4;
      if (currentPage >= totalPages - 2) startPage = totalPages - 3;
      if (startPage > 2) pageNumbers.push('...');
      for (let i = startPage; i <= endPage; i++) {
        if (i > 1 && i < totalPages) pageNumbers.push(i);
      }
      if (endPage < totalPages - 1) pageNumbers.push('...');
      if (totalPages > 1) pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSelectedLevels([]);
    setSelectedCategories([]);
    setSelectedSubjects([]);
    setSelectedContentTypes([]);
    setSelectedQuarters([]);
    setSelectedSpecialiteNames([]);
    setSearchTerm('');
    setPriceRange([0, maxPrice]);
    setShowFilters(false);
    setCurrentPage(1);
  };

  const handleAddToBasket = async (documentId) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      toast.error(t('checkout.errors.login_required'));
      navigate('/login');
      return;
    }

    try {
      const response = await axios.post(
        `${config.SITE_API_URL}/add-to-basket`,
        { documentId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-api-key': config.SITE_API_KEY,
          },
        }
      );
      toast.success(response.data.message);
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.restricted) {
        toast.warning(error.response.data.message);
      } else if (error.response?.status === 400 && error.response.data.message.includes("déjà dans votre panier")) {
        toast.info(error.response.data.message);
      } else {
        toast.error(error.response?.data?.message || "Erreur lors de l'ajout au panier");
      }
    }
  };

  const DocumentCard = ({ doc, onAddToBasket }) => {
    const uniqueLevels = [...new Set(doc.level || [])];
    const uniqueCategories = [...new Set(doc.category || [])];
    const specialiteName = doc.specialiteNames?.[0] || null;

    return (
      <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
        <Link to={`/detailcourse/${doc.id}`}>
          <div className="relative bg-white rounded-t-lg overflow-hidden">
            <ImageWithAuth
              src={doc.image || '/img/file-20210323-14-1u3ei2y.avif'}
              alt={doc.title}
              className="w-full h-48 object-cover"
            />
            {doc.news && (
              <span className="absolute top-3 left-3 bg-yellow-400 text-yellow-900 text-xs px-3 py-1 rounded-full font-medium">
                {t('document_details.new')}
              </span>
            )}
            {doc.best_seller && (
              <span className="absolute top-3 right-3 bg-red-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                {t('document_details.best_seller')}
              </span>
            )}
            {doc.discount > 0 && (
              <span className="absolute bottom-3 left-3 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                {doc.discount}%
              </span>
            )}
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2 mb-2">
              {doc.subject && (
                <span className="text-xs text-pink-500 font-medium bg-pink-50 px-2 py-1 rounded">
                  {doc.subject}
                </span>
              )}
              {uniqueLevels.map((lvl) => (
                <span key={`${doc.id}-lvl-${lvl}`} className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                  {lvl}
                </span>
              ))}
              {uniqueCategories.map((cat) => (
                <span key={`${doc.id}-cat-${cat}`} className="text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700">
                  {cat}
                </span>
              ))}
              {specialiteName && (
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-purple-50 text-purple-700">
                  {specialiteName}
                </span>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2 mb-2">
              {doc.contentType && (
                <span className="text-xs text-red-600 font-medium bg-red-50 px-2 py-1 rounded">
                  {doc.contentType}
                </span>
              )}
              {doc.quarter && (
                <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                  {doc.quarter}
                </span>
              )}
            </div>
            
            <h3 className="font-semibold text-lg text-gray-900 mb-2 line-clamp-2 dir-rtl:text-right">
              {doc.title}
            </h3>
            <p className="text-sm text-gray-600 mb-3 line-clamp-2 dir-rtl:text-right">
              {doc.description}
            </p>
            <div className="flex items-center justify-between flex-row-reverse dir-rtl:flex-row-reverse">
              <span className="text-lg font-bold text-gray-900">{doc.price.toFixed(3)} {t('DT')}</span>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  onAddToBasket(doc.id);
                }} 
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
              >
                {t('course.buy_button')}
              </button>
            </div>
          </div>
        </Link>
      </div>
    );
  };

  const DocumentList = ({ doc, onAddToBasket }) => {
    const uniqueLevels = [...new Set(doc.level || [])];
    const uniqueCategories = [...new Set(doc.category || [])];
    const specialiteName = doc.specialiteNames?.[0] || null;

    return (
      <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-4">
        <Link to={`/detailcourse/${doc.id}`}>
          <div className="flex items-center space-x-4 dir-rtl:space-x-reverse">
            <div className="flex-shrink-0 relative">
              <ImageWithAuth
                src={doc.image || '/placeholder.svg'}
                alt={doc.title}
                className="w-32 h-32 object-cover rounded"
              />
              {doc.best_seller && (
                <span className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 text-xs px-1 py-0.5 rounded-full font-medium dir-rtl:-left-1 dir-rtl:right-auto">
                  ★
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between flex-row-reverse dir-rtl:flex-row-reverse">
                <div className="flex-1">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {uniqueLevels.map((lvl) => (
                      <span key={`${doc.id}-lvl-${lvl}`} className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded">
                        {lvl}
                      </span>
                    ))}
                    {uniqueCategories.map((cat) => (
                      <span key={`${doc.id}-cat-${cat}`} className="text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700">
                        {cat}
                      </span>
                    ))}
                    {doc.subject && (
                      <span className="text-xs text-gray-500 font-medium bg-gray-50 px-2 py-1 rounded">
                        {doc.subject}
                      </span>
                    )}
                    {doc.contentType && (
                      <span className="text-xs text-red-600 font-medium bg-red-50 px-2 py-1 rounded">
                        {doc.contentType}
                      </span>
                    )}
                    {doc.quarter && (
                      <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded">
                        {doc.quarter}
                      </span>
                    )}
                    {specialiteName && (
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-purple-50 text-purple-700">
                        {specialiteName}
                      </span>
                    )}
                  </div>
                  
                  <h3 className="font-semibold text-gray-900 text-lg mb-1 dir-rtl:text-right">
                    {doc.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2 dir-rtl:text-right">
                    {doc.description}
                  </p>
                </div>
                <div className="flex flex-col items-end space-y-2 mx-4 dir-rtl:mr-4 dir-rtl:ml-0">
                  <span className="text-xl font-bold text-gray-900">
                    {doc.price.toFixed(3)}{t('DT')}
                  </span>
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      onAddToBasket(doc.id);
                    }} 
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
                  >
                    {t('course.buy_button')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  };

  const Pagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = getPageNumbers();

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>
            {i18n.language === 'ar' ? (
              `عرض ${((currentPage - 1) * itemsPerPage) + 1} - ${Math.min(currentPage * itemsPerPage, filteredDocuments.length)} من أصل ${filteredDocuments.length} ${filteredDocuments.length > 1 ? 'نتائج' : 'نتيجة'}`
            ) : (
              `Affichage de ${((currentPage - 1) * itemsPerPage) + 1} - ${Math.min(currentPage * itemsPerPage, filteredDocuments.length)} sur ${filteredDocuments.length} ${filteredDocuments.length > 1 ? 'résultats' : 'résultat'}`
            )}
          </span>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="ml-2 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={20}>20</option>
            <option value={30}>30</option>
            <option value={40}>40</option>
            <option value={50}>50</option>
          </select>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-2 rounded-md text-sm ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            {direction === 'rtl' ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>

          {pageNumbers.map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' && goToPage(page)}
              className={`px-3 py-2 rounded-md text-sm ${page === '...' ? 'text-gray-400 cursor-default' : page === currentPage ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              disabled={page === '...'}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 rounded-md text-sm ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            {direction === 'rtl' ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>
    );
  };

  // Vérifier si des spécialités doivent être affichées
  const showSpecialites = hasClassRequiringSpecialite();

  return (
    <div style={{...props.style, direction: direction}}>
      <m.div className="h-[380px] overflow-hidden sm:h-[365px] relative" {...fadeIn}>
        <Parallax
          className="lg-no-parallax bg-cover absolute top-0 left-0 w-full h-full"
          translateY={[-40, 40]}
          style={{ backgroundImage: `url(/assets/img/webp/portfolio-bg.webp)` }}
        />
        <Container className="h-full relative">
          <Row className="items-stretch justify-center h-[450px] md:h-[375px] sm:h-[350px]">
            <Col xl={6} lg={7} md={8} xs={12} className="text-center flex justify-center flex-col">
              <h2 className="text-blue-900 mb-[0px] font-serif font-medium -tracking-[1px] my-auto xs:mb-[0px]">
                {t('course.page_title')}
              </h2>
            </Col>
          </Row>
        </Container>
      </m.div>
      
      <ToastContainer position="bottom-left" />

      <div className="min-h-screen bg-gray-50">
        <div className="max-w-[90%] mx-auto px-4 min-sm:px-6 min-lg:px-8 py-8">
          <div className="flex flex-col min-lg:flex-row gap-8">
            <div className={`min-lg:w-80 space-y-6 ${showFilters ? 'block' : 'hidden min-lg:block'}`}>
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">{t('course.filters')}</h2>
                  <button onClick={resetFilters} className="text-sm text-blue-600 hover:text-blue-800 dir-rtl:text-right">
                    {t('course.reset_filters')}
                  </button>
                </div>

                {/* Search */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-blue-900 mb-2 dir-rtl:text-right">
                    {t('course.search_label')}
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('course.search_placeholder')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dir-rtl:text-right"
                  />
                </div>

                {/* Levels */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-blue-900 mb-3 dir-rtl:text-right">
                    {t('course.school_levels')}
                  </h3>
                  <div className="space-y-2">
                    {Object.keys(levelCategories).map(level => {
                      const count = countDocumentsByLevel(level);
                      return (
                        <label key={level} className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedLevels.includes(level)}
                            onChange={() => handleLevelSelect(level)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-blue-900 capitalize">{level}</span>
                          <span className="ml-auto text-xs text-blue-500">({count})</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Classes */}
                {selectedLevels.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-blue-900 mb-3 dir-rtl:text-right">
                      {t('course.classes')}
                    </h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selectedLevels.flatMap((level) =>
                        (levelCategories[level] || []).map((category) => {
                          const count = countDocumentsByCategory(category);
                          const classEntry = Object.entries(subLevelsData).find(
                            ([_, data]) => data.name === category
                          );
                          const classId = classEntry?.[0];
                          const requiresSpecialite = classId && CLASSES_REQUIRING_SPECIALITE.includes(classId);
                          
                          return (
                            <label key={category} className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedCategories.includes(category)}
                                onChange={() => handleCategorySelect(category, level)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm text-blue-900">
                                {category}
                              </span>
                              <span className="ml-auto text-xs text-blue-500">({count})</span>
                            </label>
                          );
                        }),
                      )}
                    </div>
                  </div>
                )}

                {/* Specialités - AFFICHAGE CONDITIONNEL */}
                {showSpecialites && selectedCategories.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-blue-900 mb-3 dir-rtl:text-right">
                      {t('course.specialities') || 'الاختصاصات'}
                    </h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {selectedCategories.map((categoryName) => {
                        const classEntry = Object.entries(subLevelsData).find(
                          ([_, data]) => data.name === categoryName
                        );
                        const classId = classEntry?.[0];
                        
                        if (!classId || !CLASSES_REQUIRING_SPECIALITE.includes(classId)) {
                          return null;
                        }
                        
                        const specialites = specialitesByClass[classId] || [];
                        
                        if (specialites.length === 0) {
                          return (
                            <div key={categoryName} className="text-sm text-gray-500 italic">
                              {categoryName}: {t('course.no_specialities') || 'لا توجد اختصاصات'}
                            </div>
                          );
                        }
                        
                        return (
                          <div key={categoryName} className="border-t pt-2 first:border-t-0">
                            <div className="font-semibold text-sm text-gray-700 mb-2 dir-rtl:text-right">
                              {categoryName}:
                            </div>
                            {specialites.map((spec) => {
                              const count = documents.filter(doc => 
                                doc.specialiteNames && doc.specialiteNames.includes(spec.name)
                              ).length;
                              
                              return (
                                <label key={spec.id} className="flex items-center ml-4 mb-1 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={selectedSpecialiteNames.includes(spec.name)}
                                    onChange={() => toggleSpecialite(spec.name)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span className="ml-2 text-sm text-gray-600 flex-1 dir-rtl:mr-2">
                                    {spec.name}
                                  </span>
                                  <span className="text-xs text-blue-500">
                                    ({count})
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                    {isLoadingSpecialites && (
                      <div className="text-center py-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                      </div>
                    )}
                  </div>
                )}

                {/* Subjects - avec comptage dynamique */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-blue-900 mb-3 dir-rtl:text-right">
                    {t('course.subjects')}
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {[...new Set(documents.map(d => d.subject).filter(Boolean))].map(sub => {
                      const count = countDocumentsBySubjectDynamic(sub);
                      return (
                        <label key={sub} className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedSubjects.includes(sub)}
                            onChange={() => toggleSubject(sub)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-blue-900">{sub}</span>
                          <span className="ml-auto text-xs text-blue-500">({count})</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Content Types - avec comptage dynamique */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-blue-900 mb-3 dir-rtl:text-right">
                    {t('course.content_types')}
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {[...new Set(contentTypes.map(ct => 
                      ct.type?.name?.[i18n.language] || ct.type?.name?.fr || ''
                    ).filter(Boolean))].map((contentType) => {
                      const count = countDocumentsByContentTypeDynamic(contentType);
                      return (
                        <label key={contentType} className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedContentTypes.includes(contentType)}
                            onChange={() => toggleContentType(contentType)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-blue-900">{contentType}</span>
                          <span className="ml-auto text-xs text-blue-500">({count})</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Quarters - avec comptage dynamique */}
                {selectedLevels.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-blue-900 mb-3 dir-rtl:text-right">
                      {t('course.quarters')}
                    </h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {quarters.map((quarter) => {
                        const quarterName = quarter.name?.[i18n.language] || quarter.name?.fr || '';
                        const count = countDocumentsByQuarterDynamic(quarterName);
                        return (
                          <label key={quarter._id} className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedQuarters.includes(quarterName)}
                              onChange={() => toggleQuarter(quarterName)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-blue-900">{quarterName}</span>
                            <span className="ml-auto text-xs text-blue-500">({count})</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Price */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-blue-900 mb-3 dir-rtl:text-right">
                    {t('course.price_label')}
                  </h3>
                  <div className="space-y-2">
                    <input
                      type="range"
                      min="0"
                      max={maxPrice}
                      value={priceRange[1]}
                      onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      disabled={maxPrice === 0}
                    />
                    <div className="flex justify-between text-xs text-blue-500 dir-rtl:flex-row-reverse">
                      <span>0 {t('DT')}</span>
                      <span>{priceRange[1]} {t('DT')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex flex-col min-sm:flex-row min-sm:items-center min-sm:justify-between gap-4">
                  <div className="flex items-center justify-between space-x-4 flex-row-reverse dir-rtl:flex-row-reverse">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="min-lg:hidden flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 dir-rtl:space-x-reverse"
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                      <span>{t('course.filters')}</span>
                    </button>
                    <span className="text-sm text-gray-700">
                      {t('course.documents_found', { count: filteredDocuments.length })}
                    </span>
                  </div>

                  <div className="flex items-center space-x-4 dir-rtl:space-x-reverse">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 mx-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dir-rtl:text-right"
                    >
                      <option value="all">{t('course.sort_by.all')}</option>
                      <option value="new">{t('document_details.new')}</option>
                      <option value="featured">{t('document_details.best_seller')}</option>
                      <option value="price-low">{t('course.sort_by.price_low')}</option>
                      <option value="price-high">{t('course.sort_by.price_high')}</option>
                    </select>

                    <div className="flex border border-gray-300 rounded-md">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`px-3 py-2 text-sm ${viewMode === 'grid' ? 'bg-[#f29f2f] text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        <Grid className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`px-3 py-2 text-sm xxs:hidden ${viewMode === 'list' ? 'bg-[#f29f2f] text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                      >
                        <List className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Documents Grid/List */}
              {paginatedDocuments.length === 0 ? (
                <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                  <div className="text-gray-400 mb-4">
                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('course.no_documents_found')}</h3>
                  <p className="text-gray-500">{t('course.try_different_criteria')}</p>
                </div>
              ) : (
                <>
                  <div className={viewMode === 'grid' ? 'grid grid-cols-1 min-sm:grid-cols-2 min-lg:grid-cols-3 min-xl:grid-cols-4 gap-6' : 'space-y-4'}>
                    {paginatedDocuments.map((doc) => (
                      viewMode === 'grid' ? (
                        <DocumentCard key={doc.id} doc={doc} onAddToBasket={handleAddToBasket} />
                      ) : (
                        <DocumentList key={doc.id} doc={doc} onAddToBasket={handleAddToBasket} />
                      )
                    ))}
                  </div>
                  <Pagination />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Course;